import { alertSubmitLabel, submitAlertChannels, retainAlertChannelSelection, renderAlertOutcomes } from './alert-channels.js';
import { initializePhoneCountry, isPhoneInput } from './alert-phone-country.js';

const ENDPOINT = '/apps/sigma-stock-alerts/roasters/';
let guestRehearsalToken = typeof window !== 'undefined' ? window.takeRoasterGuestRehearsal?.() || '' : '';
if (typeof window !== 'undefined') window.addEventListener('pagehide', () => { guestRehearsalToken = ''; });
const CONSENT_VERSION = 'roaster-alerts-v3-button-opt-in-2026-09-06';
const CONSENT = {
  email: 'By selecting “Create email alert”, you agree to recurring emails from Sigma Coffee UK about new coffees from this roaster. Unsubscribe at any time.',
  sms: 'By selecting “Create text alert”, you agree to recurring promotional texts from Sigma Coffee UK about new coffees from this roaster. Frequency varies. Consent is not a condition of purchase. Message and data rates may apply. Unsubscribe at any time.',
};

export function roasterSubmitLabel(channel) {
  return channel === 'sms' ? 'Create text alert' : 'Create email alert';
}

// Friendly format check only. The server validates supported-country mobile metadata.
export function normalizeRoasterSmsInput(value) {
  return isPhoneInput(value) ? value.trim() : null;
}

export function roasterSignupMessage(channel, roasterName = 'this roaster') {
  return channel === 'sms'
    ? 'Alerts start once SMS permission is recorded. Unsubscribe in any text.'
    : `You’ve saved email alerts for new coffees from ${roasterName}.`;
}

export function roasterAccountError(code) {
  if (code === 'sign_in_required') return 'Your sign-in has expired. Refresh this page to sign in again or continue without an account.';
  if (code === 'account_email_mismatch') return 'Use your current store account email. Refresh this page if your email has changed.';
  if (code === 'account_email_unavailable') return 'We couldn’t verify your store account email. Check your account details and try again, or sign out to use guest signup.';
  if (code === 'account_link_unavailable') return 'We couldn’t save this alert to your account. Use an existing alert email to manage it, or contact us for help.';
  return null;
}

export function roasterSignupChannels(payload, smsPoliciesAvailable) {
  if (!Array.isArray(payload.channels) || !payload.channels.includes('email')) return [];
  return smsPoliciesAvailable && payload.channels.includes('sms') ? ['email', 'sms'] : ['email'];
}

// Only the signed-account endpoint can establish account ownership and live status.
// A saved preference does not establish provider consent or handset delivery.
export function hasSavedRoasterAlert(payload, collection) {
  return payload?.ok === true && Array.isArray(payload.subscriptions)
    && payload.subscriptions.some(row => row.collection_gid === collection
      && row.account_linked === true && row.status === 'active'
      && ['email', 'sms'].includes(row.channel) && row.preferences?.new_coffees === true);
}

export async function readSavedRoasterChannels(collection, request) {
  let after;
  const seen = new Set();
  const saved = new Set();
  do {
    const payload = await request('status', { collection_gid: collection, ...(after ? { after } : {}) });
    if (payload?.ok !== true || !Array.isArray(payload.subscriptions)) throw new Error('invalid_status');
    for (const row of payload.subscriptions) {
      if (hasSavedRoasterAlert({ ok: true, subscriptions: [row] }, collection)) saved.add(row.channel);
    }
    after = payload.next_cursor;
    if (after && (typeof after !== 'string' || seen.has(after) || seen.size >= 20)) throw new Error('invalid_cursor');
    if (after) seen.add(after);
  } while (after);
  return [...saved];
}
export async function readSavedRoasterAlert(collection, request) {
  return (await readSavedRoasterChannels(collection, request)).length > 0;
}

// This receipt is presentation-only. Never store destinations or capabilities here,
// or send this value to an API as proof of ownership or current subscription status.
export function createRoasterGuestReceipt(collection, getStorage) {
  const key = `sigma:roaster-alert-receipt:${collection}`;
  let channels = [];
  const read = () => {
    try {
      const raw = getStorage().getItem(key);
      let receipt;
      try { receipt = JSON.parse(raw); } catch { channels = []; return channels; }
      channels = receipt?.version === 1 && Array.isArray(receipt.channels)
        ? [...new Set(receipt.channels.filter(channel => channel === 'email' || channel === 'sms'))] : [];
    } catch { /* Storage may be blocked. Keep a successful signup for this page only. */ }
    return channels;
  };
  return {
    key,
    read,
    save(channel) {
      if (channel !== 'email' && channel !== 'sms') return;
      channels = [...new Set([...read(), channel])];
      try { getStorage().setItem(key, JSON.stringify({ version: 1, channels })); }
      catch { /* A storage failure must never turn a successful signup into an error. */ }
    },
  };
}

export async function requestRoasterAlert(action, body, fetcher = fetch) {
  const response = await fetcher(`${ENDPOINT}${action}`, {
    method: 'POST', credentials: 'same-origin', cache: 'no-store',
    referrerPolicy: 'no-referrer', redirect: 'error',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body), signal: AbortSignal.timeout(35000),
  });
  const payload = await response.json();
  if (!response.ok || payload.ok !== true) throw new Error(payload.code || 'service_unavailable');
  return payload;
}

export function initializeRoasterAlert(root) {
  if (root.dataset.roasterAlertInitialized) return;
  root.dataset.roasterAlertInitialized = 'true';
  const collection = root.dataset.collectionGid;
  const trigger = root.querySelector('[data-roaster-alert-open]');
  const dialog = root.querySelector('dialog');
  const form = root.querySelector('form');
  const error = root.querySelector('[data-roaster-alert-error]');
  const result = root.querySelector('[data-roaster-alert-result]');
  const done = root.querySelector('[data-roaster-alert-done]');
  const triggerLabel = root.querySelector('[data-roaster-alert-label]');
  const accountManage = root.querySelector('[data-roaster-account-manage]');
  const guestManage = root.querySelector('[data-roaster-guest-manage]');
  const guestStatus = root.querySelector('[data-roaster-guest-status]');
  const privateRehearsal = Boolean(guestRehearsalToken);
  let guestExpired = false;
  if (privateRehearsal) {
    guestStatus.hidden = false;
    guestStatus.textContent = 'Checking private guest test…';
  }
  const request = (action, body) => requestRoasterAlert(action, privateRehearsal
    ? { ...body, guest_rehearsal_token: guestRehearsalToken || 'invalid' } : body);
  let guestExpiryTimer;
  const expireGuest = () => {
    if (!privateRehearsal) return;
    guestExpired = true;
    guestRehearsalToken = '';
    trigger.hidden = true;
    guestManage.hidden = true;
    submit.disabled = true;
    error.textContent = 'This private test has expired. Ask for a new test link.';
    guestStatus.textContent = error.textContent;
  };
  guestManage.addEventListener('click', () => {
    if (!guestRehearsalToken) return expireGuest();
    location.assign(`${ENDPOINT}manage#roaster_guest_token=${encodeURIComponent(guestRehearsalToken)}`);
  });
  window.addEventListener('pagehide', () => { window.clearTimeout(guestExpiryTimer); guestManage.hidden = true; });
  window.addEventListener('pageshow', event => { if (event.persisted) expireGuest(); });
  window.addEventListener('sigma:roaster-guest-invalidated', () => {
    guestRehearsalToken = '';
    guestExpired = true;
    trigger.hidden = true;
    guestManage.hidden = true;
    submit.disabled = true;
    guestStatus.hidden = false;
    guestStatus.textContent = 'Open the private test link in a new tab to continue.';
    error.textContent = guestStatus.textContent;
  });
  const email = form.elements.email;
  const accountSignup = email.hasAttribute('data-roaster-account-email');
  const phone = form.elements.phone;
  const submit = form.querySelector('[type="submit"]');
  const channels = form.querySelector('fieldset');
  const channelInputs = [...form.querySelectorAll('[name="channel"]')];
  const addChannel = root.querySelector('[data-roaster-add-channel]');
  let savedChannels = [];
  const savedOutcomes = new Map();
  let retryChannels = [];
  let editing = false;
  let enabledChannels = [];
  const country = form.elements.phone_country;
  const phoneCountry = initializePhoneCountry(root.querySelector('[data-roaster-phone-field]'));
  const card = root.querySelector('.product-stock-alert__card');
  const content = card.querySelector('.product-stock-alert__intro');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let busy = false;
  let completed = false;
  let statusVersion = 0;
  let statusPending;
  let eligible = false;
  let unlinkedSavedThisPage = false;
  const receipt = createRoasterGuestReceipt(collection, () => window.localStorage);
  const setSavedTrigger = saved => {
    trigger.dataset.roasterAlertSaved = String(saved);
    triggerLabel.textContent = saved ? 'Alert saved' : 'Get roaster alerts';
    trigger.setAttribute('aria-label', saved
      ? accountSignup && !unlinkedSavedThisPage ? 'Alert saved. Manage roaster alerts' : 'Alert saved. View confirmation'
      : 'Get roaster alerts');
  };
  const showAccountState = (saved, guest = false) => {
    const changed = completed !== saved;
    setSavedTrigger(saved);
    completed = saved;
    form.hidden = saved;
    addChannel.hidden = !saved || enabledChannels.every(channel => savedChannels.includes(channel));
    done.hidden = !saved;
    done.classList.toggle('button--primary', !saved || guest);
    accountManage.hidden = !saved || guest;
    root.querySelector('h2').textContent = saved ? 'Alert saved' : 'Get roaster alerts';
    root.querySelector('.product-stock-alert__heading p').hidden = saved;
    result.textContent = !saved ? '' : guest
      ? 'You saved alerts for this roaster on this browser. To manage or cancel them, use the link in an alert email or text.'
      : 'Your alert for new coffees from this roaster is saved to your account.';
    dialog.setAttribute('aria-describedby', saved ? result.id : `${dialog.id}-description`);
    resize();
    if (changed && dialog.open) card.focus({ preventScroll: true });
  };
  const refreshGuestState = () => {
    if (accountSignup || privateRehearsal || !eligible || busy) return;
    if (editing) return;
    savedChannels = receipt.read();
    const saved = savedChannels.length > 0;
    unlinkedSavedThisPage = saved;
    showAccountState(saved, true);
  };
  const refreshAccountState = () => {
    if (!accountSignup || privateRehearsal || !eligible || busy || editing || unlinkedSavedThisPage) return Promise.resolve();
    if (statusPending) return statusPending;
    const version = statusVersion;
    statusPending = readSavedRoasterChannels(collection, request).then(saved => {
      if (root.isConnected && version === statusVersion && !busy && !editing) { savedChannels = saved; showAccountState(saved.length > 0); }
    }).catch(() => {
      // Never retain a green state after logout or an unverifiable response.
      if (root.isConnected && version === statusVersion && !busy && !editing) { savedChannels = []; showAccountState(false); }
    }).finally(() => { statusPending = undefined; });
    return statusPending;
  };
  const refreshSavedState = () => { refreshGuestState(); void refreshAccountState(); };
  const onAccountFocus = () => { if (!dialog.open) refreshSavedState(); };
  const onAccountPageShow = event => { if (event.persisted) refreshSavedState(); };
  const onReceiptChange = event => {
    if (event.key === receipt.key || event.key === null) refreshGuestState();
  };
  const onAccountPageHide = () => {
    statusVersion += 1;
    unlinkedSavedThisPage = false;
    savedChannels = [];
    editing = false;
    setSavedTrigger(false);
  };
  window.addEventListener('focus', onAccountFocus);
  window.addEventListener('pageshow', onAccountPageShow);
  window.addEventListener('pagehide', onAccountPageHide);
  window.addEventListener('storage', onReceiptChange);
  let closeTimer;
  let resizeFrame;
  let resizeTimer;
  let resizeTarget;
  const viewportKey = () => `${dialog.clientHeight}:${card.clientWidth}:${dialog.dataset.modalKeyboard || ''}`;
  let previousViewportKey;
  const finishResize = () => {
    window.cancelAnimationFrame(resizeFrame);
    window.clearTimeout(resizeTimer);
    delete dialog.dataset.stockAlertResizing;
    resizeTarget = undefined;
  };
  const resize = ({ animate = true } = {}) => {
    if (!dialog.open || dialog.dataset.stockAlertState === 'closing') return;
    const styles = window.getComputedStyle(card);
    const pixels = value => Number.parseFloat(value) || 0;
    const chrome = pixels(styles.paddingTop) + pixels(styles.paddingBottom)
      + pixels(styles.borderTopWidth) + pixels(styles.borderBottomWidth);
    const max = pixels(styles.maxHeight);
    const measured = content.offsetHeight + chrome;
    const target = Math.ceil(max > 0 ? Math.min(measured, max) : measured);
    if (resizeTarget === target && animate) return;
    const current = card.getBoundingClientRect().height;
    finishResize();
    const shouldAnimate = animate && !reducedMotion.matches
      && dialog.dataset.modalKeyboard !== 'true' && Math.abs(current - target) > 0.5;
    if (!shouldAnimate) { card.style.height = `${target}px`; return; }
    card.style.height = `${current}px`;
    card.offsetHeight;
    resizeTarget = target;
    dialog.dataset.stockAlertResizing = 'true';
    resizeFrame = window.requestAnimationFrame(() => { card.style.height = `${target}px`; });
    resizeTimer = window.setTimeout(finishResize, 260);
  };
  // The inner content can grow independently of the fixed-pixel outer card.
  // Country loading, inline errors and text resizing all use the same path.
  const contentObserver = new ResizeObserver(() => resize());
  contentObserver.observe(content);
  const onViewportResize = () => {
    const next = viewportKey();
    if (next === previousViewportKey) return;
    previousViewportKey = next;
    resize({ animate: false });
  };
  window.addEventListener('resize', onViewportResize);
  dialog.addEventListener('sigma:modal-viewport-change', onViewportResize);
  const onSectionUnload = event => {
    if (!event.target.contains(root)) return;
    contentObserver.disconnect();
    window.removeEventListener('resize', onViewportResize);
    window.removeEventListener('focus', onAccountFocus);
    window.removeEventListener('pageshow', onAccountPageShow);
    window.removeEventListener('pagehide', onAccountPageHide);
    window.removeEventListener('storage', onReceiptChange);
    document.removeEventListener('shopify:section:unload', onSectionUnload);
    finishResize();
  };
  document.addEventListener('shopify:section:unload', onSectionUnload);
  const selectedChannels = () => channelInputs.filter(input => input.checked).map(input => input.value);
  const selectedChannel = () => selectedChannels()[0] || 'email';
  const syncChannel = () => {
    const selected = selectedChannels();
    const sms = selected.includes('sms');
    const label = alertSubmitLabel(selected, selected.length > 0 && selected.every(channel => retryChannels.includes(channel)));
    submit.querySelector('[data-roaster-submit-label]').textContent = label;
    submit.dataset.buttonLoadingText = selected.length === 2 ? 'Creating alerts...' : sms ? 'Creating text alert...' : 'Creating email alert...';
    email.disabled = busy || !selected.includes('email');
    phone.disabled = busy || !sms;
    country.disabled = busy || !sms || !phoneCountry.ready();
    if (sms) void phoneCountry.refresh().then(() => {
      country.disabled = busy || !selectedChannels().includes('sms') || !phoneCountry.ready();
    });
    root.querySelector('[data-roaster-email-field]').hidden = !selected.includes('email');
    root.querySelector('[data-roaster-phone-field]').hidden = !sms;
    root.querySelector('[data-roaster-sms-legal]').hidden = !sms;
    root.querySelector('[data-roaster-consent-copy]').textContent = selected.map(channel => CONSENT[channel].replace(roasterSubmitLabel(channel), label)).join(' ');
    form.querySelectorAll('[aria-invalid]').forEach(input => input.removeAttribute('aria-invalid'));
    resize();
  };
  addChannel.addEventListener('click', () => {
    editing = true;
    completed = false;
    statusVersion += 1;
    channelInputs.forEach(input => { input.checked = enabledChannels.includes(input.value) && !savedChannels.includes(input.value); });
    form.hidden = false;
    addChannel.hidden = true;
    root.querySelector('h2').textContent = 'Add another alert';
    syncChannel();
    (selectedChannel() === 'sms' ? phone : email).focus();
  });
  const fail = (message, input) => {
    error.textContent = message;
    resize();
    if (input) input.setAttribute('aria-invalid', 'true');
    if (dialog.open) (input || error).focus();
  };
  const close = () => {
    if (!dialog.open || dialog.dataset.stockAlertState === 'closing') return;
    finishResize();
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) dialog.close();
    else {
      dialog.dataset.stockAlertState = 'closing';
      closeTimer = window.setTimeout(() => dialog.close(), 240);
    }
  };
  form.noValidate = true;
  form.querySelectorAll('[name="channel"]').forEach((input) => input.addEventListener('change', () => { retainAlertChannelSelection(channelInputs, input); if (!error.querySelector('.alert-results')) error.textContent = ''; syncChannel(); }));
  root.querySelectorAll('[data-roaster-alert-close]').forEach((button) => button.addEventListener('click', close));
  dialog.addEventListener('cancel', (event) => { event.preventDefault(); close(); });
  dialog.addEventListener('close', () => {
    window.clearTimeout(closeTimer);
    finishResize();
    delete dialog.dataset.stockAlertState;
    trigger.setAttribute('aria-expanded', 'false');
    trigger.focus();
  });
  trigger.addEventListener('click', () => {
    if (dialog.open) return;
    dialog.dataset.stockAlertState = 'opening';
    trigger.setAttribute('aria-expanded', 'true');
    dialog.showModal();
    previousViewportKey = viewportKey();
    resize({ animate: false });
    const touch = window.matchMedia('(pointer: coarse)').matches;
    (touch || completed ? root.querySelector('.product-stock-alert__card') : selectedChannel() === 'sms' ? phone : email).focus({ preventScroll: true });
    window.requestAnimationFrame(() => {
      if (dialog.open) dialog.dataset.stockAlertState = 'open';
    });
  });
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (busy || completed || guestExpired) return;
    error.textContent = '';
    form.querySelectorAll('[aria-invalid]').forEach((input) => input.removeAttribute('aria-invalid'));
    const selected = selectedChannels();
    if (!selected.length) return fail('Choose email, text message, or both.', channelInputs[0]);
    for (const channel of selected) {
      if (!enabledChannels.includes(channel)) return fail('This alert channel is unavailable. Please choose email.');
      const destination = channel === 'sms' ? phone : email;
      destination.value = destination.value.trim();
      if (channel === 'sms' && !phoneCountry.ready()) return fail('Retry loading countries or choose email.');
      if (channel === 'sms' && !normalizeRoasterSmsInput(destination.value)) return fail('Check the mobile number and country.', destination);
      if (!destination.checkValidity()) return fail(channel === 'sms' ? 'Check the mobile number and country.' : 'Enter a valid email address.', destination);
    }
    const buttonLabel = alertSubmitLabel(selected, selected.length > 0 && selected.every(channel => retryChannels.includes(channel)));
    const requests = selected.map(channel => ({ channel, body: {
      collection_gid: collection, channel, destination: channel === 'sms' ? phone.value : email.value,
      ...(channel === 'email' && accountSignup ? { account_signup: true } : {}),
      ...(channel === 'sms' ? { phone_country: country.value } : {}),
      website: form.elements.website.value,
      preferences: { new_coffees: true, restocks: false },
      consent: true, consent_version: CONSENT_VERSION, button_label: buttonLabel,
    } }));
    busy = true;
    editing = true;
    statusVersion += 1;
    submit.disabled = true;
    channels.disabled = true;
    syncChannel();
    form.setAttribute('aria-busy', 'true');
    submit.dispatchEvent(new CustomEvent('sigma:button-loading', { bubbles: true, detail: { loading: true } }));
    try {
      const outcomes = await submitAlertChannels(requests, async ({ channel, body }) => {
        const payload = await request('subscribe', body);
        if (payload.status !== 'preference_saved') throw new Error('service_unavailable');
        unlinkedSavedThisPage = unlinkedSavedThisPage || payload.account_linked !== true;
        if (payload.account_linked !== true && !privateRehearsal) receipt.save(channel);
        savedChannels = [...new Set([...savedChannels, channel])];
        return payload;
      });
      const failures = outcomes.filter(outcome => !outcome.ok);
      retryChannels = failures.map(outcome => outcome.channel);
      for (const failure of failures) {
        const code = failure.error?.message;
        failure.error = new Error(roasterAccountError(code) || (code === 'rate_limited' ? 'Too many attempts. Wait a minute, then try again.'
          : ['invalid_destination', 'supported_mobile_required'].includes(code) ? 'Check the address or mobile number.'
          : 'Please try again.'));
      }
      if (savedChannels.length) setSavedTrigger(true);
      outcomes.filter(outcome => outcome.ok).forEach(outcome => savedOutcomes.set(outcome.channel, outcome));
      result.textContent = '';
      if (!failures.length) renderAlertOutcomes(result, [...savedOutcomes.values()], 'roaster');
      if (failures.length) {
        channelInputs.forEach(input => { input.checked = failures.some(outcome => outcome.channel === input.value); });
        renderAlertOutcomes(error, [...savedOutcomes.values(), ...failures], 'roaster');
        resize();
        if (dialog.open) error.focus();
      } else {
        completed = true;
        editing = false;
        form.hidden = true;
        root.querySelector('h2').textContent = savedOutcomes.size > 1 ? 'Alert requests saved' : 'Alert request saved';
        root.querySelector('.product-stock-alert__heading p').hidden = true;
        dialog.setAttribute('aria-describedby', result.id);
        accountManage.hidden = ![...savedOutcomes.values()].some(outcome => outcome.payload.account_linked === true);
        addChannel.hidden = enabledChannels.every(channel => savedChannels.includes(channel));
        guestManage.hidden = !privateRehearsal || !guestRehearsalToken;
        done.hidden = false;
        if (dialog.open) done.focus({ preventScroll: true });
      }
    } finally {
      busy = false;
      submit.disabled = guestExpired;
      channels.disabled = false;
      syncChannel();
      form.removeAttribute('aria-busy');
      submit.dispatchEvent(new CustomEvent('sigma:button-loading', { bubbles: true, detail: { loading: false } }));
    }
  });
  syncChannel();
  request('target', { collection_gid: collection }).then((payload) => {
    if (!root.isConnected) return;
    if (payload.available !== true || payload.collection_gid !== collection || typeof payload.roaster_name !== 'string' || !payload.roaster_name.trim()) {
      if (privateRehearsal) guestStatus.textContent = 'This roaster is not available for the private test. Open the approved roaster link.';
      return;
    }
    enabledChannels = roasterSignupChannels(payload, root.dataset.roasterSmsPoliciesAvailable === 'true');
    if (!enabledChannels.length) return;
    const smsEnabled = enabledChannels.includes('sms');
    form.querySelector('[name="channel"][value="sms"]').disabled = !smsEnabled;
    root.querySelector('[data-roaster-sms-option]').hidden = !smsEnabled;
    channels.hidden = !smsEnabled;
    root.querySelector('[data-roaster-alert-name]').textContent = payload.roaster_name;
    trigger.hidden = false;
    eligible = true;
    refreshSavedState();
    if (privateRehearsal) {
      if (!guestRehearsalToken) return expireGuest();
      const remaining = Date.parse(payload.rehearsal_expires_at) - Date.now();
      if (!Number.isFinite(remaining) || remaining <= 0 || remaining > 3600000) return expireGuest();
      guestExpiryTimer = window.setTimeout(expireGuest, remaining);
      guestStatus.textContent = 'Private guest test. Use the approved email and stay signed out.';
    }
  }).catch(() => {
    // Unavailable or unverified targets never expose signup.
    if (privateRehearsal) guestStatus.textContent = 'Private test unavailable. Stay signed out and open a current test link.';
  });
}

if (typeof document !== 'undefined') {
  const initialize = () => document.querySelectorAll('[data-roaster-alert-root]').forEach(initializeRoasterAlert);
  initialize();
  document.addEventListener('shopify:section:load', initialize);
}
